/**
 * Portal Switcher Component
 * 
 * Allows users with multiple permissions to switch between portals.
 * Shows in the header/sidebar for authorized users.
 */

import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
  Building2, Users, Briefcase, DollarSign, Star, 
  Calendar, Eye, FileText, ChevronDown, Check
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { 
  PORTALS, 
  getPrimaryPortal, 
  getAccessiblePortals, 
  shouldShowPortalSwitcher 
} from '../../config/portals';
import { cn } from '../../lib/utils';

// Icon mapping
const PORTAL_ICONS = {
  executive: Building2,
  hr_compliance: Users,
  operations: Briefcase,
  finance: DollarSign,
  my_aligned: Star,
  scheduler: Calendar,
  supervisor: Eye,
  applicant: FileText,
};

export function PortalSwitcher({ className }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const userRole = user?.role;
  const additionalRoles = user?.additional_roles || [];
  
  // Check if user should see portal switcher
  if (!shouldShowPortalSwitcher(userRole, additionalRoles)) {
    return null;
  }
  
  const accessiblePortals = getAccessiblePortals(userRole, additionalRoles);
  const currentPath = location.pathname;
  
  // Find current portal based on path
  const currentPortal = accessiblePortals.find(p => currentPath.startsWith(p.path)) || 
                        getPrimaryPortal(userRole);
  
  const CurrentIcon = PORTAL_ICONS[currentPortal.id] || Building2;
  
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="outline" 
          className={cn("gap-2", className)}
          data-testid="portal-switcher"
        >
          <CurrentIcon className="h-4 w-4" />
          <span className="hidden sm:inline">{currentPortal.name}</span>
          <ChevronDown className="h-4 w-4 opacity-50" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>Switch Portal</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {accessiblePortals.map((portal) => {
          const Icon = PORTAL_ICONS[portal.id] || Building2;
          const isActive = currentPortal.id === portal.id;
          
          return (
            <DropdownMenuItem
              key={portal.id}
              onClick={() => navigate(portal.path)}
              className={cn("cursor-pointer", isActive && "bg-accent")}
              data-testid={`portal-switch-${portal.id}`}
            >
              <div className={cn("h-8 w-8 rounded mr-3 flex items-center justify-center", portal.color)}>
                <Icon className="h-4 w-4 text-white" />
              </div>
              <div className="flex-1">
                <p className="font-medium">{portal.name}</p>
                <p className="text-xs text-muted-foreground">{portal.description}</p>
              </div>
              {isActive && <Check className="h-4 w-4 ml-2" />}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * Portal Redirect Component
 * 
 * Placed at the root to redirect users to their appropriate portal
 * based on role after login.
 */
export function PortalRedirect() {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  
  React.useEffect(() => {
    if (isAuthenticated && user?.role) {
      const portal = getPrimaryPortal(user.role);
      navigate(portal.path, { replace: true });
    }
  }, [isAuthenticated, user, navigate]);
  
  return null;
}

export default PortalSwitcher;
